import type { ReactNode } from 'react';

import { Badge } from '@/components/atoms/Badge';
import { Card } from '@/components/atoms/Card';
import { Overline } from '@/components/atoms/Eyebrow';
import { ChartTitleProvider } from '@/components/charts/ChartTitleContext';

import {
    SurfaceQualityDot,
    type SurfaceQualityRating,
} from '../BioscopeQualityDot/BioscopeQualityDot';

import styles from './FeedingChartPanel.module.scss';

/**
 * `quality` is the data-quality state for the indicator (IPS req 11), with
 * `qualitySubject: 'pellet'` on a pellet panel. Omit it for a panel that shows no
 * Bioscope-derived series, so the dot means something when present.
 */
type FeedingChartPanelProps = SurfaceQualityRating & {
    title: string;
    /**
     * Short state label shown beside the title, for a condition that changes how every
     * figure in the panel should be read. Used for weekly bucketing, which was
     * previously announced only in a caption below the chart and was routinely missed.
     */
    badge?: string;
    children: ReactNode;
};

/**
 * Heading + data-quality indicator above a feeding chart.
 *
 * The Opto chart components take a `chartName` but render no heading of their own
 * (it names the PNG export), so there was nowhere to put the indicator req 11
 * requires on graphs. Callers keep passing `chartName` for the export filename and
 * pass the same string here for the visible title.
 */
export function FeedingChartPanel(props: FeedingChartPanelProps) {
    const { title, badge, children } = props;

    return (
        <Card fullWidth={false} className={styles.panel}>
            <div className={styles.header}>
                <Overline as="h2" size="md" className={styles.title}>
                    {title}
                </Overline>
                <SurfaceQualityDot rating={props} />
                {badge ? <Badge>{badge}</Badge> : null}
            </div>
            <ChartTitleProvider title={title}>{children}</ChartTitleProvider>
        </Card>
    );
}
